import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllUserNames } from "../../utils/account";
import { getCurrentSubscription, getPaidWorkspaces } from "../../utils/billing";
import { getAllWorkspaceNames } from "../../utils/workspaces";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { buildUserNameMap, getDisplayName } from "../../utils/userNames";

const pad = (value: number) => value.toString().padStart(2, "0");

const formatDateTime = (value?: string) => {
  if (!value) return "-";

  const date = new Date(value);

  return `${pad(date.getDate())}-${pad(
    date.getMonth() + 1,
  )}-${date.getFullYear()} ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}:${pad(date.getSeconds())}`;
};

const buildWorkspaceInfoMap = (
  workspaces: any[] = [],
  userNames: Record<string, string> = {},
) =>
  workspaces.reduce<Record<string, { name: string; owner: string }>>(
    (map, workspace) => {
      const id = workspace.id || workspace._id;
      if (id) {
        map[id] = {
          name: workspace.name || "-",
          owner: getDisplayName(workspace.addedBy, userNames),
        };
      }
      return map;
    },
    {},
  );

const enrichPaidWorkspace = async (item: any) => {
  if (!item.workspaceId) return item;

  try {
    const response = await getCurrentSubscription(item.workspaceId);
    return {
      ...item,
      nextBillingDate: response.data?.plan?.subscriptionEndDate || null,
    };
  } catch (error) {
    console.error("Error fetching next billing date:", {
      workspaceId: item.workspaceId,
      error,
    });
    return item;
  }
};

export default function PaidUsers() {
  const navigate = useNavigate();
  const [paidWorkspaces, setPaidWorkspaces] = useState<any[]>([]);
  const [workspaceInfo, setWorkspaceInfo] = useState<
    Record<string, { name: string; owner: string }>
  >({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPaidWorkspaces = async () => {
      try {
        setLoading(true);
        const [response, workspaceResponse, usersResponse] = await Promise.all([
          getPaidWorkspaces(),
          getAllWorkspaceNames().catch((error) => {
            console.error("Error fetching workspace names:", error);
            return { workspaces: [] };
          }),
          getAllUserNames().catch((error) => {
            console.error("Error fetching user names:", error);
            return { users: [] };
          }),
        ]);
        const paidWorkspaceList = response.data || [];
        const enrichedPaidWorkspaces = await Promise.all(
          paidWorkspaceList.map(enrichPaidWorkspace),
        );
        const userNames = buildUserNameMap(usersResponse.users || []);
        setWorkspaceInfo(
          buildWorkspaceInfoMap(workspaceResponse.workspaces || [], userNames),
        );
        setPaidWorkspaces(enrichedPaidWorkspaces);
      } catch (error) {
        console.error("Error fetching paid workspaces:", error);
        setPaidWorkspaces([]);
        setWorkspaceInfo({});
      } finally {
        setLoading(false);
      }
    };

    fetchPaidWorkspaces();
  }, []);

  return (
    <div className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-[24px] font-semibold text-[#211f26]">Paid Users</h1>
        <p className="text-[13px] text-[#84828e]">
          Workspaces with non-free billing subscriptions.
        </p>
      </div>

      <div className="rounded-2xl border border-[#eae7ec] bg-white p-5">
        {loading ? (
          <div className="text-[14px] text-[#84828e]">Loading...</div>
        ) : (
          <>
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b-0">
                  <TableHead className="bg-[#faf9fb]">S.No.</TableHead>
                  <TableHead className="bg-[#faf9fb]">Workspace ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Workspace Name</TableHead>
                  <TableHead className="bg-[#faf9fb]">Owner</TableHead>
                  {/* <TableHead className="bg-[#faf9fb]">Status</TableHead> */}
                  <TableHead className="bg-[#faf9fb]">Customer ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">
                    Subscription ID
                  </TableHead>
                  <TableHead className="bg-[#faf9fb]">Product ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Price ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Payment Mode</TableHead>
                  {/* <TableHead className="bg-[#faf9fb]">Credits</TableHead> */}
                  <TableHead className="bg-[#faf9fb]">
                    Negotiated Price
                  </TableHead>
                  {/* <TableHead className="bg-[#faf9fb]">Is Free Plan</TableHead> */}
                  <TableHead className="bg-[#faf9fb]">
                    Next Billing Date
                  </TableHead>
                  <TableHead className="bg-[#faf9fb]">Created At</TableHead>
                  <TableHead className="bg-[#faf9fb]">Updated At</TableHead>
                  <TableHead className="bg-[#faf9fb]">Billing</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {paidWorkspaces.map((item: any, index: number) => (
                  <TableRow
                    key={
                      item._id ||
                      item.stripeSubscriptionId ||
                      `${item.workspaceId}-${index}`
                    }
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.workspaceId || "-"}</TableCell>
                    <TableCell>
                      {item.workspaceId
                        ? workspaceInfo[item.workspaceId]?.name || "-"
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {item.workspaceId
                        ? workspaceInfo[item.workspaceId]?.owner || "-"
                        : "-"}
                    </TableCell>
                    {/* <TableCell>{item.status || "-"}</TableCell> */}
                    <TableCell>
                      {item.stripeCustomerId || item.customerId || "-"}
                    </TableCell>
                    <TableCell>
                      {item.stripeSubscriptionId || item.subscriptionId || "-"}
                    </TableCell>
                    <TableCell>
                      {item.stripeProductId ||
                        item.productId ||
                        item.metadata?.productId ||
                        "-"}
                    </TableCell>
                    <TableCell>
                      {item.stripePriceId ||
                        item.priceId ||
                        item.metadata?.priceId ||
                        "-"}
                    </TableCell>
                    <TableCell>{item.metadata?.paymentMode || "-"}</TableCell>
                    {/* <TableCell>{item.metadata?.credits ?? "-"}</TableCell> */}
                    <TableCell>
                      {item.metadata?.negotiatedPrice ?? "-"}
                    </TableCell>
                    {/* <TableCell>
                      {getBooleanText(item.metadata?.isFreePlan)}
                    </TableCell> */}
                    <TableCell>
                      {formatDateTime(item.nextBillingDate)}
                    </TableCell>
                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                    <TableCell>{formatDateTime(item.updatedAt)}</TableCell>
                    <TableCell>
                      <button
                        className="text-[#211f26] underline"
                        onClick={() => navigate(`/billing/${item.workspaceId}`)}
                      >
                        View Billing
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {paidWorkspaces.length === 0 && (
              <div className="pt-4 text-[14px] text-[#84828e]">
                No paid users found.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
